/**
 * What was done to this article, and by whom (#1744 point 7).
 *
 * ── WHY THIS IS ON THE RECORD AND NOT ONLY IN THE DRAWER ────────────────────
 * The drawer's Timeline is `activity.contextual_timeline` — channels,
 * directions, subjects, bodies. It answers "who said what about this", which is
 * a genuinely different question from "who published this and when". Measured:
 * that door returns communications and has no governed act in it at all.
 *
 * ── ACTS AND CHANGES, SHOWN AS TWO THINGS ───────────────────────────────────
 * `service.kb_article_history` returns both and labels each `act` or `change`,
 * because universal change capture writes a `record.updated` for the very same
 * UPDATE a governed verb performs. Dropping the changes would lose the edits
 * that have no verb — a corrected title — and showing them undifferentiated
 * would double every entry and make one publication look like two events.
 *
 * So the acts are the spine, and the changes are available beneath them rather
 * than interleaved. A reader looking for "when did this go live" should not
 * have to read past three `record.updated` rows to find it.
 */
import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button, PageSection } from '@solum/ui'
import { useSolum } from '../data/client'
import { QueryBoundary, queryState } from '../data/QueryBoundary'
import s from './KbArticleHistory.module.css'

/** Exactly what `service.kb_article_history` returns. */
export interface KbHistoryRow {
  at: string
  kind: string
  action: string
  actor_id: string | null
  actor_name: string | null
  from_status: string | null
  reason: string | null
}

/**
 * The server's action keys, said in words.
 *
 * An unknown action renders its own key rather than vanishing — the same rule
 * the acts bar and the assets panel hold to. A new governed verb appearing in
 * a history as a raw key is a small ugliness; appearing as nothing is a hole in
 * an accountability record.
 */
const ACTION_TEXT: Record<string, string> = {
  'service.kb_article.submitted_for_review': 'Submitted for review',
  'service.kb_article.withdrawn_from_review': 'Withdrawn from review',
  'service.kb_article.published': 'Published',
  'service.kb_article.retracted': 'Retracted',
  'service.kb_article.retired': 'Retired',
  'record.created': 'Created',
  'record.updated': 'Edited',
  'record.deleted': 'Deleted',
}

/** A time a person can read, in their own locale, with the full value on hover. */
function when(iso: string): { text: string; title: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { text: iso, title: iso }
  return { text: d.toLocaleString(), title: d.toISOString() }
}

export function KbArticleHistory({ id }: { id: string }): ReactNode {
  const { client } = useSolum()
  const raw = client.raw()
  const history = useQuery({
    queryKey: ['kb', 'history', id],
    queryFn: () => raw.rpcTable<KbHistoryRow>('kb_article_history', { p_id: id }, 'service'),
    retry: false,
  })
  /* `queryState` returns an OBJECT and never null, so `if (state)` is ALWAYS
     true — the shape `KbArticleProcess` gets right and I got wrong in all three
     of these panels. Every one of them rendered an empty boundary for ever, and
     987 unit tests stayed green because they drive the PURE view directly.
     A browser found it in one navigation. Guard on `.kind`. */
  const gate = queryState([history])
  if (gate.kind !== 'ready') {
    return (
      <QueryBoundary queries={[history]} label="this article’s history">
        {() => null}
      </QueryBoundary>
    )
  }
  return <KbHistoryView rows={history.data ?? []} />
}

/** The pure half, so the property can be asserted with no client. */
export function KbHistoryView({ rows }: { rows: KbHistoryRow[] }): ReactNode {
  const [showChanges, setShowChanges] = useState(false)
  const acts = rows.filter((r) => r.kind === 'act')
  const changes = rows.filter((r) => r.kind !== 'act')

  if (rows.length === 0) {
    return (
      <PageSection title="History" headingLevel={3} testId="kb-history">
        <div className={s.panel}>
        <p className={s.empty} data-testid="kb-history-empty">Nothing has been done to this article yet.</p>
        </div>
      </PageSection>
    )
  }

  return (
    <PageSection title="History" headingLevel={3} testId="kb-history">
      <div className={s.panel}>

      {acts.length === 0 ? (
        <p className={s.empty} data-testid="kb-history-no-acts">
          This article has been edited but never submitted, published or retired.
        </p>
      ) : (
        <ol className={s.list} data-testid="kb-history-acts">
          {acts.map((r, i) => <Entry key={`a${i}`} row={r} />)}
        </ol>
      )}

      {changes.length > 0 && (
        <>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={showChanges}
            onClick={() => setShowChanges((v) => !v)}
            data-testid="kb-history-toggle"
          >
            {showChanges ? 'Hide' : 'Show'} {changes.length} recorded {changes.length === 1 ? 'change' : 'changes'}
          </Button>
          {showChanges && (
            <ol className={s.list} data-testid="kb-history-changes">
              {changes.map((r, i) => <Entry key={`c${i}`} row={r} muted />)}
            </ol>
          )}
        </>
      )}
      </div>
    </PageSection>
  )
}

function Entry({ row, muted = false }: { row: KbHistoryRow; muted?: boolean }): ReactNode {
  const t = when(row.at)
  return (
    <li className={muted ? s.entryMuted : s.entry} data-testid={`kb-history-${row.action}`}>
      <span className={s.what}>{ACTION_TEXT[row.action] ?? row.action}</span>
      {row.from_status && <span className={s.from}>from {row.from_status}</span>}
      <span className={s.who} data-testid="kb-history-who">
        {/* An act with no human actor is the SYSTEM — the scheduler, an ETL.
            Saying so is the honest answer; leaving it blank would read as
            missing data about something that is fully recorded. */}
        {row.actor_name ?? (row.actor_id ? 'a colleague' : 'the system')}
      </span>
      <time className={s.at} dateTime={row.at} title={t.title}>{t.text}</time>
      {row.reason && <span className={s.reason}>“{row.reason}”</span>}
    </li>
  )
}
